import { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import { useFormContext } from 'react-hook-form';
import SingleSelect from '../../components/GlobalFilterBar/SingleSelect/SingleSelect';
import { SingleSelectGroup } from '../../components/GlobalFilterBar/types';
import * as styles from './styles';

// Enums
enum FormulaElementType {
  Metric = 'field',
  Operator = 'operator',
  Number = 'number',
  Parenthesis = 'parenthesis',
}

export enum FormulaTokenType {
  Parenthesis = 'parenthesis',
  Field = 'field',
  Operator = 'operator',
  Number = 'number',
}

enum Parenthesis {
  Open = '(',
  Close = ')',
}

type ChipProps = {
  color: 'error' | 'default' | 'info' | 'success' | 'primary';
  variant: 'filled' | 'outlined';
};

interface FormulaElement {
  id: string;
  type: FormulaElementType;
  value: string;
  displayValue: string;
}

export interface FormulaToken {
  type: FormulaTokenType;
  value: string | number;
  dataset?: string;
}

interface FormulaBuilderProps {
  metrics?: SingleSelectGroup[];
  disabled?: boolean;
  error?: string;
  onErrorChange?: (error: string) => void;
  onChange?: (tokens: FormulaToken[]) => void;
  dataSet?: string;
}

export interface FormulaBuilderRef {
  validateFormula: () => boolean;
  getFormulaTokens: () => FormulaToken[];
  clearFormula: () => void;
  setFormulaTokens: (tokens: FormulaToken[]) => void;
}

// Constants
const OPERATORS = [
  { label: '+', value: '+' },
  { label: '-', value: '-' },
  { label: '*', value: '*' },
  { label: '/', value: '/' },
  { label: '(', value: '(' },
  { label: ')', value: ')' },
] as const;

const OPERATOR_NAMES: Record<string, string> = {
  '+': 'Plus',
  '-': 'Minus',
  '*': 'Multiply',
  '/': 'Divide',
  '(': 'Left Parenthesis',
  ')': 'Right Parenthesis',
};

// Helper functions
const calculateParenthesisBalance = (elements: FormulaElement[]): number =>
  elements.reduce((balance, el) => {
    if (el.type === FormulaElementType.Parenthesis) {
      return balance + (el.value === Parenthesis.Open ? 1 : -1);
    }
    return balance;
  }, 0);

const isValueElement = (element?: FormulaElement) =>
  element?.type === FormulaElementType.Number ||
  element?.type === FormulaElementType.Metric ||
  (element?.type === FormulaElementType.Parenthesis && element?.value === Parenthesis.Close);

const canAddValueAfter = (element?: FormulaElement) =>
  !element ||
  element.type === FormulaElementType.Operator ||
  (element.type === FormulaElementType.Parenthesis && element.value === Parenthesis.Open);

const getChipProps = (element: FormulaElement, isActive: boolean, hasError: boolean): ChipProps => {
  if (hasError) return { color: 'error' as const, variant: 'filled' as const };
  if (!isActive) return { color: 'default' as const, variant: 'outlined' as const };
  const colorMap = {
    [FormulaElementType.Metric]: 'info' as const,
    [FormulaElementType.Number]: 'success' as const,
    [FormulaElementType.Operator]: 'primary' as const,
    [FormulaElementType.Parenthesis]: 'primary' as const,
  };
  return { color: colorMap[element.type], variant: 'filled' as const };
};

function convertFromTokens(tokens: FormulaToken[]): FormulaElement[] {
  if (!tokens) return [];
  const result = tokens.map((token) => {
    if (token.type === FormulaTokenType.Field) {
      return {
        id: uuidv4(),
        type: FormulaElementType.Metric,
        value: token.value as string,
        displayValue: token.value as string,
      };
    }
    if (token.type === FormulaTokenType.Number) {
      const numValue = String(token.value);
      return {
        id: uuidv4(),
        type: FormulaElementType.Number,
        value: numValue,
        displayValue: numValue,
      };
    }
    if (token.type === FormulaTokenType.Parenthesis) {
      const paren = token.value as string;
      return {
        id: uuidv4(),
        type: FormulaElementType.Parenthesis,
        value: paren,
        displayValue: paren,
      };
    }
    // operator
    const op = token.value as string;
    return {
      id: uuidv4(),
      type: FormulaElementType.Operator,
      value: op,
      displayValue: op,
    };
  });
  return result;
}

const convertToTokens = (elements: FormulaElement[], dataSet?: string): FormulaToken[] =>
  elements.map((element) => {
    if (element.type === FormulaElementType.Metric) {
      return {
        type: FormulaTokenType.Field,
        value: element.value,
        dataset: dataSet,
      };
    }
    if (element.type === FormulaElementType.Number) {
      return {
        type: FormulaTokenType.Number,
        value: Number.parseFloat(element.value),
      };
    }
    if (element.type === FormulaElementType.Parenthesis) {
      return {
        type: FormulaTokenType.Parenthesis,
        value: element.value,
      };
    }
    // operator
    return {
      type: FormulaTokenType.Operator,
      value: element.value,
    };
  });

const hasChipError = (
  element: FormulaElement,
  index: number,
  formulaElements: FormulaElement[],
  error: string
): boolean => {
  const isLastElement = index === formulaElements.length - 1;

  if (!error) return false;

  // Last element is operator or opening parenthesis (Invalid sequence)
  if (
    isLastElement &&
    (element.type === FormulaElementType.Operator ||
      (element.type === FormulaElementType.Parenthesis && element.value === Parenthesis.Open))
  ) {
    return true;
  }

  // Unmatched opening parenthesis (for parenthesis balance errors)
  if (
    error.includes('Unmatched') &&
    element.type === FormulaElementType.Parenthesis &&
    element.value === Parenthesis.Open
  ) {
    const balance = calculateParenthesisBalance(formulaElements.slice(0, index + 1));
    return balance > 0;
  }

  return false;
};

const getOperatorButtonDisabledState = (
  operator: string,
  disabled: boolean,
  validationState: {
    canAddOperator: boolean;
    canAddOpenParenthesis: boolean;
    canAddCloseParenthesis: boolean;
  }
): boolean => {
  if (operator === Parenthesis.Open) {
    return disabled || !validationState.canAddOpenParenthesis;
  }
  if (operator === Parenthesis.Close) {
    return disabled || !validationState.canAddCloseParenthesis;
  }
  return disabled || !validationState.canAddOperator;
};

const isOperatorDisabledInPopover = (
  operator: string,
  editingIndex: number,
  formulaElements: FormulaElement[]
): boolean => {
  const prevElement = editingIndex > 0 ? formulaElements[editingIndex - 1] : undefined;
  const nextElement = editingIndex < formulaElements.length - 1 ? formulaElements[editingIndex + 1] : undefined;

  if (operator === Parenthesis.Open) {
    return !canAddValueAfter(prevElement);
  }

  if (operator === Parenthesis.Close) {
    const balance = calculateParenthesisBalance(formulaElements.slice(0, editingIndex));
    return balance <= 0 || !isValueElement(prevElement);
  }

  // Regular operators (+, -, *, /) need a value before them
  if (!isValueElement(prevElement)) {
    return true;
  }

  // If there's a next element, it must be something that can come after an operator
  // (number, metric, or opening parenthesis) - NOT another operator or closing parenthesis
  if (nextElement) {
    const isNextOperator = nextElement.type === FormulaElementType.Operator;
    const isNextClosingParen =
      nextElement.type === FormulaElementType.Parenthesis && nextElement.value === Parenthesis.Close;
    if (isNextOperator || isNextClosingParen) {
      return true;
    }
  }

  return false;
};

const FormulaBuilder = forwardRef<FormulaBuilderRef, FormulaBuilderProps>(
  ({ metrics = [], disabled = false, error = '', onErrorChange, onChange, dataSet }, ref) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { watch, setValue, resetField } = useFormContext();
    const [formulaElements, setFormulaElements] = useState<FormulaElement[]>([]);
    const [insertNumber, setInsertNumber] = useState('');
    const [editingElementId, setEditingElementId] = useState<string | null>(null);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [operatorAnchorEl, setOperatorAnchorEl] = useState<HTMLElement | null>(null);
    const [metricSelectKey, setMetricSelectKey] = useState(0);
    const [lastAddedId, setLastAddedId] = useState<string | null>(null);
    const [editingNumberId, setEditingNumberId] = useState<string | null>(null);
    const [editingNumberValue, setEditingNumberValue] = useState('');
    const editMetricContainerRef = useRef<HTMLDivElement>(null);
    const editNumberInputRef = useRef<HTMLInputElement>(null);

    const selectedMetric = watch('selectedMetric');
    const editMetric = watch('editMetric');

    const updateFormula = (newElements: FormulaElement[], newLastAddedId?: string | null) => {
      setFormulaElements(newElements);
      setLastAddedId(newLastAddedId ?? newElements.at(-1)?.id ?? null);
      onErrorChange?.('');
      // Pass fresh tokens to onChange callback instead of relying on getFormulaTokens()
      const freshTokens = convertToTokens(newElements, dataSet);
      onChange?.(freshTokens);
    };

    // Validation state - determines what can be added
    const validationState = useMemo(() => {
      const last = formulaElements.at(-1);
      const balance = calculateParenthesisBalance(formulaElements);
      const trimmed = insertNumber.trim();
      const hasValidNumber = trimmed !== '' && trimmed !== '.' && Number(trimmed) >= 0;

      return {
        canAddOperator: isValueElement(last),
        canAddOpenParenthesis: canAddValueAfter(last),
        canAddCloseParenthesis: balance > 0 && isValueElement(last),
        canAddNumber: canAddValueAfter(last) && hasValidNumber,
        canAddMetric: canAddValueAfter(last),
      };
    }, [formulaElements, insertNumber]);

    const handleOperatorClick = (operator: string) => {
      if (disabled) return;
      const newElement: FormulaElement = {
        id: uuidv4(),
        type:
          operator === Parenthesis.Open || operator === Parenthesis.Close
            ? FormulaElementType.Parenthesis
            : FormulaElementType.Operator,
        value: operator,
        displayValue: operator,
      };
      updateFormula([...formulaElements, newElement], newElement.id);
    };

    const handleAddNumber = () => {
      if (disabled || !insertNumber.trim()) return;
      const newElement: FormulaElement = {
        id: uuidv4(),
        type: FormulaElementType.Number,
        value: insertNumber.trim(),
        displayValue: insertNumber.trim(),
      };
      updateFormula([...formulaElements, newElement], newElement.id);
      setInsertNumber('');
    };

    const handleDeleteElement = (id: string) => {
      updateFormula(formulaElements.filter((el) => el.id !== id));
    };

    const handleClearFormula = () => {
      updateFormula([]);
    };

    const handleMetricClick = (id: string, event: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      setEditingElementId(id);
      setAnchorEl(event.currentTarget);
      // Reset the editMetric field to clear any previous selections
      setValue('editMetric', []);
      // Programmatically open the metric selection dropdown by clicking its button
      setTimeout(() => editMetricContainerRef.current?.querySelector('button')?.click(), 0);
    };

    const handleChipClick = (element: FormulaElement, event: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      setEditingElementId(element.id);

      if (element.type === FormulaElementType.Metric) {
        handleMetricClick(element.id, event);
      } else if (element.type === FormulaElementType.Operator || element.type === FormulaElementType.Parenthesis) {
        setOperatorAnchorEl(event.currentTarget);
      } else if (element.type === FormulaElementType.Number) {
        setEditingNumberId(element.id);
        setEditingNumberValue(element.value);
        // Wait for TextField to render before focusing and selecting content
        setTimeout(() => {
          editNumberInputRef.current?.focus();
          editNumberInputRef.current?.select();
        }, 0);
      }
    };

    const handleNumberCancel = () => {
      setEditingNumberId(null);
      setEditingElementId(null);
      setEditingNumberValue('');
    };

    const handleNumberSave = () => {
      if (!editingNumberId || !editingNumberValue.trim()) {
        handleNumberCancel();
        return;
      }

      const trimmedValue = editingNumberValue.trim();
      updateFormula(
        formulaElements.map((el) =>
          el.id === editingNumberId ? { ...el, value: trimmedValue, displayValue: trimmedValue } : el
        )
      );
      setEditingNumberId(null);
      setEditingElementId(null);
      setEditingNumberValue('');
    };

    const handleOperatorSelect = (operator: string) => {
      if (!editingElementId) return;

      const isParenthesis = operator === Parenthesis.Open || operator === Parenthesis.Close;
      updateFormula(
        formulaElements.map((el) =>
          el.id === editingElementId
            ? {
                ...el,
                type: isParenthesis ? FormulaElementType.Parenthesis : FormulaElementType.Operator,
                value: operator,
                displayValue: operator,
              }
            : el
        )
      );
      setOperatorAnchorEl(null);
      setEditingElementId(null);
    };

    // Render helper functions
    const renderNumberEditor = (elementId: string) => (
      <Box key={elementId} sx={styles.numberEditorContainerSx}>
        <Typography variant='body2' sx={styles.numberEditorLabelSx}>
          {t('navItems.customMetricsPage.number')}
        </Typography>
        <TextField
          inputRef={editNumberInputRef}
          value={editingNumberValue}
          onChange={(e) => {
            const { value } = e.target;
            if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
              setEditingNumberValue(value);
            }
          }}
          onBlur={handleNumberSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleNumberSave();
            } else if (e.key === 'Escape') {
              handleNumberCancel();
            } else if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
              e.preventDefault();
            }
          }}
          variant='standard'
          size='small'
          sx={styles.numberEditorTextFieldSx}
          type='number'
          inputMode='decimal'
          inputProps={{
            min: 0,
            step: 0.01,
          }}
        />
      </Box>
    );

    const renderFormulaChip = (element: FormulaElement, isLastElement: boolean, chipProps: ChipProps) => (
      <Chip
        key={element.id}
        label={element.displayValue}
        onDelete={isLastElement && !disabled ? () => handleDeleteElement(element.id) : undefined}
        onClick={(event) => handleChipClick(element, event)}
        {...chipProps}
        size='medium'
        sx={styles.chipSx}
        data-testid={`formula-chip-${element.type}`}
      />
    );

    const renderFormulaElement = (element: FormulaElement, index: number) => {
      const isLastElement = index === formulaElements.length - 1;
      const isActive = element.id === lastAddedId || element.id === editingElementId;
      const hasError = hasChipError(element, index, formulaElements, error);

      // Show inline editor for number being edited
      if (element.type === FormulaElementType.Number && element.id === editingNumberId) {
        return renderNumberEditor(element.id);
      }

      const chipProps = getChipProps(element, isActive, hasError);
      return renderFormulaChip(element, isLastElement, chipProps);
    };

    // Expose validation method to parent via ref
    useImperativeHandle(ref, () => ({
      validateFormula: () => {
        // Check if formula is empty
        if (formulaElements.length === 0) {
          onErrorChange?.('');
          return true;
        }

        // Check if formula ends with an operator or opening parenthesis
        const lastElement = formulaElements.at(-1);
        if (
          lastElement?.type === FormulaElementType.Operator ||
          (lastElement?.type === FormulaElementType.Parenthesis && lastElement?.value === Parenthesis.Open)
        ) {
          onErrorChange?.(t('navItems.customMetricsPage.invalidSequence'));
          return false;
        }

        // Check parenthesis balance
        const balance = calculateParenthesisBalance(formulaElements);
        if (balance !== 0) {
          onErrorChange?.(t('navItems.customMetricsPage.unmatchedParentheses'));
          return false;
        }

        onErrorChange?.('');
        return true;
      },
      getFormulaTokens: () => convertToTokens(formulaElements, dataSet),
      clearFormula: () => {
        setFormulaElements([]);
        setInsertNumber('');
        setLastAddedId(null);
        onErrorChange?.('');
        // Notify parent with empty tokens array
        onChange?.([]);
      },
      setFormulaTokens: (tokens: FormulaToken[]) => {
        const elements = convertFromTokens(tokens);
        setFormulaElements(elements);
        onErrorChange?.('');
      },
    }));

    // Watch for metric selection changes (adding new metric)
    useEffect(() => {
      if (Array.isArray(selectedMetric) && selectedMetric.length > 0 && selectedMetric[0]?.label) {
        const { label } = selectedMetric[0];
        const newElement: FormulaElement = {
          id: uuidv4(),
          type: FormulaElementType.Metric,
          value: label,
          displayValue: label,
        };
        updateFormula([...formulaElements, newElement], newElement.id);
        resetField('selectedMetric');
        setMetricSelectKey((prev) => prev + 1);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMetric]);

    // Watch for metric edit changes (updating existing metric)
    useEffect(() => {
      if (Array.isArray(editMetric) && editMetric.length > 0 && editMetric[0]?.label && editingElementId) {
        const { label } = editMetric[0];
        updateFormula(
          formulaElements.map((el) => (el.id === editingElementId ? { ...el, value: label, displayValue: label } : el))
        );
        setEditingElementId(null);
        setAnchorEl(null);
        setValue('editMetric', []);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMetric]);

    return (
      <Box sx={styles.containerBoxSx}>
        {/* Heading */}
        <Typography variant='h6' sx={styles.headingSx}>
          {t('navItems.customMetricsPage.formula')}
        </Typography>

        {/* Description */}
        <Typography variant='body2' sx={styles.descriptionSx}>
          {t('navItems.customMetricsPage.formulaDescription')}
        </Typography>

        {/* Formula Text Box - Full Width Row */}
        <Box sx={styles.formulaContainerSx}>
          <Typography variant='caption' sx={styles.formulaLabelSx(error, theme)}>
            {t('navItems.customMetricsPage.formula')}
            {' *'}
          </Typography>
          <Box sx={styles.formulaBoxSx(error, theme)}>
            {formulaElements.length === 0
              ? null
              : formulaElements.map((element, index) => renderFormulaElement(element, index))}
          </Box>
          {error && (
            <Typography variant='caption' sx={styles.errorTextSx}>
              {error}
            </Typography>
          )}
        </Box>

        {/* Positioned SingleSelect for editing metrics */}
        {anchorEl &&
          (() => {
            const rect = anchorEl.getBoundingClientRect();
            return (
              <Box ref={editMetricContainerRef} sx={styles.metricEditorContainerSx(rect)}>
                <Box sx={styles.metricEditorHiddenBoxSx}>
                  <SingleSelect
                    options={metrics}
                    buttonText='Edit Metric'
                    controllerName='editMetric'
                    titleText={dataSet || 'Select Metric'}
                    useReset={false}
                  />
                </Box>
              </Box>
            );
          })()}

        {/* Operator Selection Popover */}
        <Popover
          open={Boolean(operatorAnchorEl)}
          anchorEl={operatorAnchorEl}
          onClose={() => {
            setOperatorAnchorEl(null);
            setEditingElementId(null);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <List sx={styles.operatorListSx}>
            {OPERATORS.map((op) => {
              const editingIndex = formulaElements.findIndex((el) => el.id === editingElementId);
              const isDisabled = isOperatorDisabledInPopover(op.value, editingIndex, formulaElements);

              return (
                <ListItem key={op.value} disablePadding>
                  <ListItemButton
                    onClick={() => handleOperatorSelect(op.value)}
                    disabled={isDisabled}
                    sx={styles.operatorListItemButtonSx}
                  >
                    <ListItemText primary={OPERATOR_NAMES[op.value]} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Popover>

        {/* Controls Row - All on a single line */}
        <Box sx={styles.controlsContainerSx}>
          <Typography variant='caption' sx={styles.controlsLabelSx}>
            {t('navItems.customMetricsPage.insertNumber')}
          </Typography>
          <Box sx={styles.controlsBoxSx}>
            {/* Insert Number Text Box */}
            <TextField
              value={insertNumber}
              onChange={(e) => {
                const { value } = e.target;
                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                  setInsertNumber(value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                  e.preventDefault();
                }
              }}
              variant='outlined'
              size='small'
              type='number'
              placeholder='0'
              inputMode='decimal'
              inputProps={{
                min: 0,
                step: 0.01,
              }}
              data-testid='insert-number-textbox'
              sx={styles.insertNumberTextFieldSx}
              disabled={disabled}
            />

            {/* Add Button */}
            <Button
              variant='outlined'
              onClick={handleAddNumber}
              data-testid='add-button'
              sx={styles.addButtonSx}
              disabled={disabled || !validationState.canAddNumber}
            >
              {t('navItems.customMetricsPage.add')}
            </Button>

            {/* + Metric SingleSelect */}
            <Tooltip
              title={!validationState.canAddMetric && !disabled ? t('navItems.customMetricsPage.addMetricTooltip') : ''}
              arrow
            >
              <Box sx={styles.tooltipWrapperSx}>
                <SingleSelect
                  key={metricSelectKey}
                  options={metrics}
                  buttonText={t('navItems.customMetricsPage.addMetric')}
                  controllerName='selectedMetric'
                  titleText={dataSet || 'Select Metric'}
                  useReset={false}
                  disabled={disabled || !validationState.canAddMetric}
                />
              </Box>
            </Tooltip>

            {/* Operator Buttons */}
            {OPERATORS.map((op) => {
              const isDisabled = getOperatorButtonDisabledState(op.value, disabled, validationState);

              return (
                <Button
                  key={op.value}
                  variant='outlined'
                  onClick={() => handleOperatorClick(op.value)}
                  data-testid={`operator-${op.value}`}
                  sx={styles.operatorButtonSx}
                  disabled={isDisabled}
                >
                  {op.label}
                </Button>
              );
            })}

            {/* Clear Formula Button */}
            <Box sx={styles.clearButtonContainerSx}>
              <Button
                variant='text'
                size='small'
                color='error'
                onClick={handleClearFormula}
                disabled={disabled || formulaElements.length === 0}
                data-testid='clear-formula-button'
                sx={styles.clearButtonSx}
              >
                {t('navItems.customMetricsPage.clearFormula')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);

FormulaBuilder.displayName = 'FormulaBuilder';

FormulaBuilder.defaultProps = {
  metrics: [],
  disabled: false,
  error: '',
  onErrorChange: undefined,
  onChange: undefined,
  dataSet: undefined,
};

export default FormulaBuilder;
